import { useMemo } from 'react';
import useSWR from 'swr';
// utils
import { fetcher, fetcherLegacy } from 'src/utils/axios';
// import SnackbarUtils from 'src/utils/snackbar-utils-configurator';

interface IFetchResult<ResultType> {
  data?: ResultType;
  isLoading: boolean;
  error: any;
  isValidating: boolean;
  refetch: () => Promise<ResultType | undefined>;
}
const useGetData = <ParamType, ResultType>({
  endpoint,
  params,
  isLegacy = false,
  keepPreviousData = false,
}: {
  endpoint: string | null;
  params?: ParamType;
  isLegacy?: boolean;
  /**
   * Hold the previous key's data while the new one loads, instead of returning undefined.
   * For a control that swaps one parameter — a period toggle, a filter — that is the difference
   * between the panel dimming and the panel vanishing and shoving the page up. Off by default:
   * every other caller changes its key only when it means to show something else entirely.
   */
  keepPreviousData?: boolean;
}): IFetchResult<ResultType> => {
  const fetcherFn = isLegacy ? fetcherLegacy : fetcher;
  let URL;

  // NAT-2497: `enabled` is a caller-side flag — it decides whether the hook
  // fetches at all, by way of a null endpoint — and never a request
  // parameter. Stripped here, once, for every hook that spreads its params:
  // left in, it rides out as `?enabled=true` AND joins the SWR key, so a
  // `mutate` built from the real parameters (as `refetchCart` does) misses
  // the entry and every post-write refresh is a silent no-op.
  const query = useMemo(() => {
    if (!params) return params;
    const { enabled, ...rest } = params as Record<string, unknown> & { enabled?: boolean };
    return (enabled === undefined ? params : (rest as ParamType)) as ParamType;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params ?? null)]);

  if (!endpoint) {
    URL = null;
  } else if (query) {
    URL = [endpoint, { params: query }];
  } else {
    URL = [endpoint];
  }
  const { data, error, isLoading, isValidating, mutate } = useSWR<ResultType>(URL, fetcherFn, {
    keepPreviousData,
  });

  // if (error) {
  //   SnackbarUtils.error(`${error?.response?.data?.message || JSON.stringify(error || '{}')}`);
  // }

  const memoizedValue = useMemo(
    () => ({
      data,
      error,
      isLoading,
      isValidating,
      refetch: mutate,
    }),
    [data, error, isLoading, isValidating, mutate],
  );
  return memoizedValue;
};

export default useGetData;
